import { KeyboardShortcut } from "@nova/constants"
import { Fragment } from "react"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"

/**
 * Ideally, this will support nice visuals of keyboard shortcuts that contain more than a
 * singular letter. Will need designs for this, including symbols: +, CMD, SHIFT, return, etc.
 */
export const Tooltip = ({
    title,
    shortcut,
    maxWidth,
    rowGapInRem,
    textClass,
    children,
    placement = "bottom",
    hoverable = false,
}: {
    title?: string
    shortcut?: KeyboardShortcut
    maxWidth?: number
    placement?: "top" | "bottom" | "left" | "right"

    /**
     * The gap to apply between each row of text,
     * as divided by the `\n` character.
     * @example 0.5 (equivalent to 0.5rem, 8px)
     */
    rowGapInRem?: number

    textClass?: string
    hoverable?: boolean
} & React.PropsWithChildren) => {
    // left/right placement was quickly hacked in for a specific purpose that required a non-middle-aligned caret (OrthomosaicPreviewFiles)
    // a proper solution would be default middle aligned with a prop to control container offset and caret display point (the 10px and 10% values I hardcoded)
    const isSide = placement === "left" || placement === "right"
    const verticalClasses = "left-1/2 -translate-x-1/2"

    const gapSide =
        placement === "top"
            ? "bottom"
            : placement === "bottom"
              ? "top"
              : placement === "left"
                ? "right"
                : "left"

    return (
        <div
            className={classNames(
                "absolute transform  bg-gray-900 border border-gray-700 rounded-lg shadow-tooltip select-none z-[1550] invisible group-hover/tooltip:visible",
                {
                    "pointer-events-none": !hoverable,
                    "whitespace-nowrap": maxWidth === undefined,
                    ["-top-[10px]"]: isSide,
                    [verticalClasses]: !isSide,
                }
            )}
            style={{
                [gapSide]: "calc(100% + 0.3125rem)",
                width: maxWidth,
            }}
        >
            {hoverable && (
                <div
                    className="absolute"
                    style={{
                        [isSide ? "width" : "height"]: "0.625rem",
                        [isSide ? "height" : "width"]: "100%",
                        [gapSide]: "-0.625rem",
                    }}
                />
            )}
            {/* Centered Tooltip Caret */}
            <div
                className={classNames(
                    "absolute w-2 h-2 rotate-45 bg-gray-900 border border-gray-700 z-0",
                    {
                        "-bottom-1": placement === "top",
                        "-top-1": placement === "bottom",
                        "-left-1": placement === "right",
                        "-right-1": placement === "bottom",
                        ["top-[10%] -translate-y-[10%]"]: isSide,
                        [verticalClasses]: !isSide,
                    }
                )}
            />
            <div
                className={classNames("absolute bottom-0  w-3 h-3 bg-gray-900", {
                    "bottom-0 rotate-180": placement === "top",
                    "top-0": placement !== "top",
                    ["top-[10%] -translate-y-[10%]"]: isSide,
                    [verticalClasses]: !isSide,
                })}
            />

            {/* Tooltip Body */}
            <div className="relative flex flex-col gap-2 justify-center items-center z-10 px-2 py-1 mt-px">
                {/* Each tooltip row would go here. For now, just one.*/}

                <div
                    className="flex flex-col"
                    style={{ gap: rowGapInRem ? `${rowGapInRem}rem` : undefined }}
                >
                    {title
                        ? title.split("\n").map((title, i) => (
                              <div
                                  key={i}
                                  className={classNames("flex gap-1.5 items-center", {
                                      "break-words": maxWidth !== undefined,
                                  })}
                              >
                                  <small
                                      className={classNames(
                                          { "text-gray-50": !textClass },
                                          textClass
                                      )}
                                  >
                                      {title}
                                  </small>

                                  {shortcut && (
                                      <div className="flex">
                                          {shortcut.letters.map((letter, i) => (
                                              <Fragment key={i}>
                                                  {i > 0 && (
                                                      <Icons.ExtraSmallIcon
                                                          Icon={Icons.Plus}
                                                          className="text-gray-500"
                                                      />
                                                  )}
                                                  <small className="text-gray-500">{letter}</small>
                                              </Fragment>
                                          ))}
                                      </div>
                                  )}
                              </div>
                          ))
                        : children}
                </div>
            </div>
        </div>
    )
}
